import * as React from 'react';
import { Badge, Button, Card, Col, Row } from 'reactstrap';
import { HttpRequest } from '../../../util/http';
import { HierarchyIdType, IHierarchy, IHierarchyStudy } from '../../hierarchies/interfaces';
import { ContentCardHeader } from '../../ui/ContentCardHeader';
import { DataList, DataListRowLink } from '../../ui/DataList';
import { FontAwesomeIcon } from '../../ui/FontAwesomeIcon';
import { GetAllHierarchies, GetAllHierarchyStudies, GetAllStudies } from '../../util';
import { IStudy, StudyIdType } from '../interfaces';

interface IHierarchyStudiesMap {
  [hierarchyId: string]: StudyIdType[];
}

interface IHierarchyStudiesEditorState {
  hierarchies: IHierarchy[]
  studies: IStudy[]
  hierarchyStudies: IHierarchyStudy[]
  selectedHierarchyId: HierarchyIdType
  hierarchyStudiesMapping: IHierarchyStudiesMap
  loading: boolean
}

export class HierarchyStudiesEditor extends React.Component<{}, IHierarchyStudiesEditorState> {
  state: IHierarchyStudiesEditorState = {
    hierarchies: [],
    studies: [],
    hierarchyStudies: [],
    selectedHierarchyId: '',
    hierarchyStudiesMapping: {},
    loading: true
  }
  componentWillMount() {
    const promises: Array<Promise<any>> = [
      this.loadHierarchies(),
      this.loadStudies(),
      this.loadHierarchyStudies(),
    ]

    Promise.all(promises).then(() => this.setState({ loading: false }))
  }

  loadHierarchyStudies(toggleLoading = false) {
    if (toggleLoading) {
      this.setState({ loading: true })
    }

    const loadPromise = GetAllHierarchyStudies().then((hierarchyStudies) => {
      this.state.hierarchyStudiesMapping = {}

      hierarchyStudies.forEach((hStudy) => {
        this.togglePair(hStudy.hierarchy, hStudy.studyId, true)
      })

      this.setState({ hierarchyStudies })
    });

    if (toggleLoading) {
      loadPromise.then(() => this.setState({ loading: false }))
    }

    return loadPromise
  }

  loadStudies() {
    return GetAllStudies().then((studies) => this.setState({ studies }))
  }

  loadHierarchies() {
    return GetAllHierarchies().then((hierarchies) => {
      hierarchies.sort((h1, h2) => h1.hierarchy.localeCompare(h2.hierarchy))

      const selectedHierarchyId = hierarchies[0].hierarchy

      this.setState({ hierarchies, selectedHierarchyId })
    })
  }

  togglePair(hierarchyId: HierarchyIdType, studyId: StudyIdType, disableRemove = false) {
    const { hierarchyStudiesMapping } = this.state

    if (!(hierarchyId in hierarchyStudiesMapping)) {
      hierarchyStudiesMapping[hierarchyId] = []
    }

    const indexOfStudy = hierarchyStudiesMapping[hierarchyId].indexOf(studyId)

    if (indexOfStudy >= 0) {
      if (!disableRemove) {
        hierarchyStudiesMapping[hierarchyId].splice(indexOfStudy, 1)

        if (hierarchyStudiesMapping[hierarchyId].length === 0) {
          delete hierarchyStudiesMapping[hierarchyId]
        }
      }
    } else {
      hierarchyStudiesMapping[hierarchyId].push(studyId)
    }

    this.setState({ hierarchyStudiesMapping })
  }

  isChecked(hierarchyId: HierarchyIdType, studyId: StudyIdType) {
    const { hierarchyStudiesMapping } = this.state

    if (!(hierarchyId in hierarchyStudiesMapping)) {
      return false
    }

    return hierarchyStudiesMapping[hierarchyId].indexOf(studyId) >= 0
  }

  countChecked(hierarchyId: HierarchyIdType) {
    const { hierarchyStudiesMapping } = this.state

    if (!(hierarchyId in hierarchyStudiesMapping)) {
      return 0
    }

    return hierarchyStudiesMapping[hierarchyId].length
  }

  async save() {
    const { hierarchyStudies, hierarchyStudiesMapping, hierarchies, studies } = this.state
    const hierarchiesToAdd: Array<{ hierarchy: string, studyId: StudyIdType }> = []
    const hierarchiesToDelete: number[] = []

    this.setState({ loading: true })

    //Get Hierarchy Studies to delete and add
    hierarchies.forEach(({ hierarchy }) => {
      const hierarchyStudiesMap = hierarchyStudiesMapping[hierarchy]

      studies.forEach(({ id: studyId }) => {
        const isSelectedInUi = hierarchyStudiesMap && hierarchyStudiesMap.indexOf(studyId) >= 0
        const isSelectedInDb = hierarchyStudies.find((hs) => {
          return hs.hierarchy === hierarchy && hs.studyId === studyId
        })

        if (!isSelectedInUi && isSelectedInDb) {
          hierarchiesToDelete.push(isSelectedInDb.id)
        } else if (isSelectedInUi && !isSelectedInDb) {
          hierarchiesToAdd.push({ hierarchy, studyId })
        }
      })
    })

    //Add Hierarchy Studies
    for (const { hierarchy, studyId: study } of hierarchiesToAdd) {
      const method = 'POST'
      const data = { hierarchy, study }
      await HttpRequest.fetch<void>('/scripts/hierarchies_studies/add', { method, data })
    }

    //Delete Hierarchy Studies
    // tslint:disable-next-line:variable-name
    for (const hierarchy_study of hierarchiesToDelete) {
      const method = 'POST'
      const data = { hierarchy_study }
      await HttpRequest.fetch<void>('/scripts/hierarchies_studies/delete', { method, data })
    }

    await this.loadHierarchyStudies()

    this.setState({ loading: false })
  }

  render() {
    const { hierarchies, studies, selectedHierarchyId, loading } = this.state

    return (
      <Card>
        <ContentCardHeader
          className="d-md-flex justify-content-between"
          sticky
          borderBottom
          header="Hierarchy Studies"
          loading={loading}
          headerActions={
            <section>
              <Button color="primary"
                disabled={loading}
                onClick={() => this.save()}>
                Save
              </Button>
              <Button color="light" className="ml-2"
                disabled={loading}
                onClick={() => this.loadHierarchyStudies(true)}>
                Cancel
              </Button>
            </section>
          } />
        <Row>
          <Col xs={5}>
            <DataList>
              {hierarchies.map(({ hierarchy }) =>
                <DataListRowLink
                  active={hierarchy === selectedHierarchyId}
                  onClick={() => this.setState({ selectedHierarchyId: hierarchy })}>
                  <Badge color="light">{this.countChecked(hierarchy).toLocaleString()}</Badge>
                  {hierarchy}
                </DataListRowLink>
              )}
            </DataList>
          </Col>
          <Col className="pt-3">
            <section style={{ position: 'sticky', top: 80 }}>
              <Row>
                {studies.map((study, index) =>
                  <React.Fragment key={study.study}>
                    {index % 2 === 0 &&
                      <div className="w-100"></div>
                    }
                    <Col>
                      <p>
                        <input type="checkbox"
                          id={study.study}
                          disabled={loading}
                          onClick={() => this.togglePair(selectedHierarchyId, study.id)}
                          checked={this.isChecked(selectedHierarchyId, study.id)}
                        />
                        <label htmlFor={study.study}>
                          <span>{study.study}&nbsp;</span>
                        </label>
                      </p>
                    </Col>
                  </React.Fragment>
                )}
              </Row>
            </section>
          </Col>
        </Row>
      </Card>
    )
  }
}
